/**
 * Изменения состава заявки на согласовании (training-change-approval; ТЗ 26.10.2026, п.1.4; ответ
 * заказчицы В20) — общее для клиента и команды:
 * — `TrainingChangeNotice`: блок «Изменения состава на согласовании» — кто и когда предложил, протокол, и
 *   по стороне «Согласовать» / «Отклонить» (тексты подтверждений — дословно из ТЗ) или «Отозвать»;
 * — `TrainingChangeEditor`: редактор изменений — тот же шаг «Программы и сотрудники», но правки
 *   копятся здесь и уходят одним предложением; внизу — «Что изменится» и «Отправить на согласование».
 * — `TrainingChangeHistory`: история изменений состава — все предложения и решения по ним (ответ
 *   заказчицы 04.11 к п.1.4: «Где протокол изменений?»).
 * Права приходят с сервера (`canDecide`, `canWithdraw`); предложение проверяет тоже сервер.
 *
 * Ответ заказчицы 04.11 к п.1.4: при отказе можно указать причину — она сохраняется в истории и
 * уходит другой стороне; команда в ответ на предложение клиента может «Предложить свой вариант» —
 * отказ и своё предложение одной операцией.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ApiError,
  companyApi,
  type EmployeeCard,
  type TrainingChange,
  type TrainingChangeLogEntry,
  type TrainingChangeOutcome,
  type TrainingChangeItemInput,
  type TrainingCompositionPatch,
  type TrainingPackageComposition,
  type TrainingProgramComposition,
  type TrainingProgramEmployee,
  type TrainingSelectedEmployee,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Alert } from '@/design-system/components/feedback/alert'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Field } from '@/design-system/components/primitives/field'
import type { StatusRole } from '@/design-system/tokens/status'
import {
  TrainingStepPrograms,
  compositionKey,
  type EmployeePickerTarget,
} from '@/training/training-request'
import { namedOfPlaces } from '@/training/selected-programs'
import { ListenerCard } from '@/training/training-step-data'
import { listenerMissing } from '@/lib/training'
import { isUnspecifiedDepartment } from '@/lib/department'

/** Кто смотрит: клиент или команда Центрики. */
export type ChangeSide = 'client' | 'team'

/** Тексты подтверждений — дословно из ТЗ (п.1.4). */
export const APPROVE_CHANGE_QUESTION = 'Подтвердить внесенные в заявку изменения?'
export const REJECT_CHANGE_QUESTION =
  'Отклонить внесенные в заявку изменения? Изменения будут потеряны'
export const WITHDRAW_CHANGE_QUESTION =
  'Предложенные изменения будут удалены, состав заявки останется прежним.'

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Кто и когда предложил изменения — с точки зрения смотрящего. */
export function changeAuthorText(c: TrainingChange, side: ChangeSide): string {
  const at = formatDate(c.at)
  if (side === 'client') {
    return c.byClient
      ? `Вы предложили изменения ${at} — их согласует Центрика.`
      : `Центрика предложила изменения ${at} — согласуйте или отклоните их.`
  }
  if (c.byClient) return `Клиент предложил изменения ${at} — согласуйте или отклоните их.`
  return c.byName
    ? `Центрика (${c.byName}) предложила изменения ${at} — их согласует клиент.`
    : `Центрика предложила изменения ${at} — их согласует клиент.`
}

export type ChangeAction = 'approve' | 'reject' | 'withdraw'

/** Причина отказа — до 1000 символов, как на сервере. */
const REASON_MAX = 1000

/** Стили многострочного поля — те же токены, что у Input. */
const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

const ACTION_DIALOG: Record<
  ChangeAction,
  { title: string; message: string; confirm: string; busy: string; failure: string }
> = {
  approve: {
    title: 'Согласовать изменения?',
    message: APPROVE_CHANGE_QUESTION,
    confirm: 'Согласовать',
    busy: 'Согласование…',
    failure: 'Не удалось согласовать изменения',
  },
  reject: {
    title: 'Отклонить изменения?',
    message: REJECT_CHANGE_QUESTION,
    confirm: 'Отклонить',
    busy: 'Отклонение…',
    failure: 'Не удалось отклонить изменения',
  },
  withdraw: {
    title: 'Отозвать изменения?',
    message: WITHDRAW_CHANGE_QUESTION,
    confirm: 'Отозвать',
    busy: 'Отзыв…',
    failure: 'Не удалось отозвать изменения',
  },
}

/**
 * Блок «Изменения на согласовании». `onAction` выполняет решение (вызывающий обновляет заказ по
 * успеху); ошибка показывается в окне подтверждения, окно остаётся открытым.
 */
export function TrainingChangeNotice({
  change,
  side,
  onAction,
  onCounter,
  className,
}: {
  change: TrainingChange
  side: ChangeSide
  /** Решение; у «Отклонить» — причина (может быть пустой). */
  onAction: (action: ChangeAction, reason?: string) => Promise<void>
  /**
   * «Предложить свой вариант» — команде в ответ на предложение клиента: открыть редактор, в котором
   * отказ и своё предложение уходят вместе. Нет обработчика — нет кнопки.
   */
  onCounter?: () => void
  className?: string
}) {
  const [pending, setPending] = useState<ChangeAction | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  async function confirm() {
    if (!pending) return
    setError(null)
    setBusy(true)
    try {
      await onAction(pending, pending === 'reject' ? reason.trim() : undefined)
      setPending(null)
      setReason('')
    } catch (err) {
      setError(getErrorMessage(err, ACTION_DIALOG[pending].failure))
    } finally {
      setBusy(false)
    }
  }

  function close() {
    if (busy) return
    setError(null)
    setPending(null)
  }

  const dialog = pending ? ACTION_DIALOG[pending] : null
  return (
    <>
      <Alert
        variant="info"
        title="Изменения состава на согласовании"
        className={className}
        actions={
          change.canDecide || change.canWithdraw ? (
            <div className="flex flex-wrap gap-2">
              {change.canDecide ? (
                <>
                  <Button size="sm" onClick={() => setPending('approve')}>
                    Согласовать
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setPending('reject')}>
                    Отклонить
                  </Button>
                  {onCounter && side === 'team' && change.byClient ? (
                    <Button size="sm" variant="secondary" onClick={onCounter}>
                      Предложить свой вариант
                    </Button>
                  ) : null}
                </>
              ) : null}
              {change.canWithdraw ? (
                <Button size="sm" variant="secondary" onClick={() => setPending('withdraw')}>
                  Отозвать
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
      >
        <p>{changeAuthorText(change, side)}</p>
        <ul className="mt-2 list-disc pl-5" aria-label="Протокол изменений">
          {change.protocol.map((e, i) => (
            <li key={i}>{e.text}</li>
          ))}
        </ul>
        <p className="mt-2 text-txt-secondary">
          Пока изменения не согласованы, состав заявки не меняется, а подтверждение данных, приём в
          работу и завершение заказа ждут решения по ним.
        </p>
      </Alert>
      <ConfirmDialog
        open={dialog !== null}
        title={dialog?.title ?? ''}
        message={dialog?.message}
        details={
          pending === 'reject' || error ? (
            <div className="flex flex-col gap-3">
              {pending === 'reject' ? (
                // Причина отказа (ответ заказчицы 04.11 к п.1.4) — необязательная; её увидит другая
                // сторона, она сохранится в истории изменений.
                <Field
                  label="Причина (необязательно)"
                  hint={side === 'team' ? 'Её увидит клиент' : 'Её увидит менеджер Центрики'}
                  className="my-0"
                >
                  {(props) => (
                    <textarea
                      {...props}
                      value={reason}
                      maxLength={REASON_MAX}
                      rows={3}
                      disabled={busy}
                      onChange={(e) => setReason(e.target.value)}
                      className={TEXTAREA_CLASS}
                    />
                  )}
                </Field>
              ) : null}
              {error ? <Alert variant="danger">{error}</Alert> : null}
            </div>
          ) : undefined
        }
        buttons={
          dialog
            ? [
                {
                  label: busy ? dialog.busy : dialog.confirm,
                  variant: pending === 'reject' ? 'danger' : 'primary',
                  disabled: busy,
                  onClick: () => void confirm(),
                },
                { label: 'Отмена', variant: 'secondary', disabled: busy, onClick: close },
              ]
            : []
        }
        onClose={close}
        closeDisabled={busy}
      />
    </>
  )
}

// ---------- История изменений ----------

/** Подпись и цвет исхода предложения. */
export const CHANGE_OUTCOME_VIEW: Record<TrainingChangeOutcome, { label: string; role: StatusRole }> = {
  pending: { label: 'На согласовании', role: 'info' },
  approved: { label: 'Согласовано', role: 'success' },
  rejected: { label: 'Отклонено', role: 'danger' },
  withdrawn: { label: 'Отозвано', role: 'neutral' },
  dropped: { label: 'Снято', role: 'neutral' },
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
}

/** Кто предложил — с точки зрения смотрящего (клиенту имён сотрудников Центрики нет). */
function historyAuthor(e: TrainingChangeLogEntry, side: ChangeSide): string {
  if (side === 'client') return e.byClient ? 'Вы' : 'Центрика'
  if (e.byClient) return e.proposedBy ? `Клиент (${e.proposedBy})` : 'Клиент'
  return e.proposedBy ? `Центрика (${e.proposedBy})` : 'Центрика'
}

/** Кто решил: «согласовал» — другая сторона, «отозвал» — автор, «снято» — без автора. */
function historyDecision(e: TrainingChangeLogEntry, side: ChangeSide): string {
  if (!e.decidedAt || e.outcome === 'pending') return ''
  const at = formatDateTime(e.decidedAt)
  if (e.outcome === 'dropped') return `${at} — снято возвратом на правки или отменой заказа`
  const decidedByClient = e.outcome === 'withdrawn' ? e.byClient : !e.byClient
  const who =
    side === 'client'
      ? decidedByClient
        ? 'вы'
        : 'Центрика'
      : decidedByClient
        ? 'клиент'
        : e.decidedBy
          ? `Центрика (${e.decidedBy})`
          : 'Центрика'
  return `${at} — ${who}`
}

/**
 * История изменений состава заказа (ответ заказчицы 04.11 к п.1.4: «Где протокол изменений?»):
 * свёрнутый блок, новые сверху; у каждого предложения — кто и когда, протокол, исход, кто и когда
 * решил, причина отказа. Пустая история — блока нет.
 */
export function TrainingChangeHistory({
  history,
  side,
  className,
}: {
  history: TrainingChangeLogEntry[] | undefined
  side: ChangeSide
  className?: string
}) {
  const [open, setOpen] = useState(false)
  if (!history || history.length === 0) return null
  return (
    <section aria-label="История изменений состава" className={cn('rounded-12 border border-line bg-panel', className)}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="ds-focus-ring flex w-full items-center gap-2 rounded-12 px-4 py-3 text-left hover:bg-hover"
      >
        <ChevronDown
          aria-hidden
          className={cn('size-4 shrink-0 stroke-current text-txt-secondary transition-transform', !open && '-rotate-90')}
        />
        <span className="flex-1 font-semibold text-txt">История изменений состава</span>
        <span className="text-13 text-txt-tertiary">{history.length}</span>
      </button>
      {open ? (
        <ol className="divide-y divide-line border-t border-line">
          {history.map((e, i) => {
            const view = CHANGE_OUTCOME_VIEW[e.outcome]
            const decision = historyDecision(e, side)
            return (
              <li key={`${e.proposedAt}-${i}`} className="flex flex-col gap-1.5 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-13 text-txt-secondary">
                    {formatDateTime(e.proposedAt)} · предложил(а): {historyAuthor(e, side)}
                  </span>
                  <Badge status={view.role}>{view.label}</Badge>
                </div>
                {e.protocol.length > 0 ? (
                  <ul className="list-disc pl-5 text-13 text-txt" aria-label="Протокол изменений">
                    {e.protocol.map((p, j) => (
                      <li key={j}>{p.text}</li>
                    ))}
                  </ul>
                ) : null}
                {decision ? <p className="text-12 text-txt-tertiary">{view.label}: {decision}</p> : null}
                {e.reason ? (
                  <p className="text-13 text-txt">
                    <span className="text-txt-secondary">Причина: </span>
                    {e.reason}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ol>
      ) : null}
    </section>
  )
}

// ---------- Редактор изменений ----------

/** Состав редактора: программы и пакеты в виде шага «Программы и сотрудники». */
export interface ChangeComposition {
  programs: TrainingProgramComposition[]
  packages: TrainingPackageComposition[]
}

/** Правка позиции из шага «Программы и сотрудники» — в состав редактора. */
export function applyCompositionPatch(
  c: ChangeComposition,
  patch: TrainingCompositionPatch,
): ChangeComposition {
  const personIds = [...new Set(patch.personIds)]
  const listenersCount = patch.listenersCount ?? personIds.length
  if ('packageId' in patch) {
    return {
      ...c,
      packages: c.packages.map((p) =>
        p.id === patch.packageId ? { ...p, selectedPersonIds: personIds, listenersCount } : p,
      ),
    }
  }
  return {
    ...c,
    programs: c.programs.map((p) =>
      p.id === patch.programId ? { ...p, selectedPersonIds: personIds, listenersCount } : p,
    ),
  }
}

/** Желаемый состав целиком — тело предложения. */
export function changeItems(c: ChangeComposition): TrainingChangeItemInput[] {
  return [
    ...c.programs
      .filter((p) => p.listenersCount > 0)
      .map((p) => ({
        programId: p.id,
        personIds: p.selectedPersonIds,
        listenersCount: p.listenersCount,
      })),
    ...c.packages
      .filter((p) => p.listenersCount > 0)
      .map((p) => ({
        packageId: p.id,
        personIds: p.selectedPersonIds,
        listenersCount: p.listenersCount,
      })),
  ]
}

interface ItemView {
  key: string
  label: string
  /** «программа» или «пакет» — для «Добавлена программа» / «Добавлен пакет». */
  pkg: boolean
  places: number
  persons: string[]
}

function itemViews(c: ChangeComposition): Map<string, ItemView> {
  const out = new Map<string, ItemView>()
  for (const p of c.packages) {
    if (p.listenersCount > 0) {
      out.set(compositionKey('package', p.id), {
        key: compositionKey('package', p.id),
        label: `пакет «${p.name}»`,
        pkg: true,
        places: p.listenersCount,
        persons: p.selectedPersonIds,
      })
    }
  }
  for (const p of c.programs) {
    if (p.listenersCount > 0) {
      out.set(compositionKey('program', p.id), {
        key: compositionKey('program', p.id),
        label: `программа «${p.shortName || p.name}»`,
        pkg: false,
        places: p.listenersCount,
        persons: p.selectedPersonIds,
      })
    }
  }
  return out
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * «Что изменится» — те же строки, что протокол сервера: добавлена, убрана, число мест, слушатели
 * (снятый и добавленный в одной позиции — замена). Авторитетный протокол строит сервер.
 */
export function changeSummary(
  before: ChangeComposition,
  after: ChangeComposition,
  personName: (personId: string) => string,
): string[] {
  const was = itemViews(before)
  const now = itemViews(after)
  const lines: string[] = []
  for (const [key, item] of now) {
    const old = was.get(key)
    if (!old) {
      const names = item.persons.map(personName)
      lines.push(
        `Добавлен${item.pkg ? '' : 'а'} ${item.label}, мест: ${item.places}` +
          (names.length > 0 ? ` (${names.join(', ')})` : ''),
      )
      continue
    }
    if (old.places !== item.places) {
      lines.push(`${capitalize(item.label)}: мест ${old.places} → ${item.places}`)
    }
    const removed = old.persons.filter((id) => !item.persons.includes(id))
    const added = item.persons.filter((id) => !old.persons.includes(id))
    while (removed.length > 0 && added.length > 0) {
      lines.push(
        `${capitalize(item.label)}: ${personName(removed.shift()!)} заменён(а) на ${personName(added.shift()!)}`,
      )
    }
    added.forEach((id) => lines.push(`${capitalize(item.label)}: добавлен(а) ${personName(id)}`))
    removed.forEach((id) => lines.push(`${capitalize(item.label)}: убран(а) ${personName(id)}`))
  }
  for (const [key, item] of was) {
    if (!now.has(key)) lines.push(`Убран${item.pkg ? '' : 'а'} ${item.label}`)
  }
  return lines
}

/**
 * Почему сотрудника нельзя выбрать в позицию — по составу редактора: пара «сотрудник + программа» в
 * заказе одна (программа пакета у него уже выбрана отдельно или в другом пакете). Нынешних
 * слушателей позиции не блокируем.
 */
export function changeBlockedFor(
  c: ChangeComposition,
  target: EmployeePickerTarget,
  personId: string,
): string | undefined {
  if (target.selectedPersonIds.includes(personId)) return undefined
  const programIds =
    target.kind === 'program'
      ? [target.id]
      : (c.packages.find((p) => p.id === target.id)?.programs.map((p) => p.id) ?? [])
  for (const pk of c.packages) {
    if (pk.listenersCount === 0 || (target.kind === 'package' && pk.id === target.id)) continue
    if (
      pk.selectedPersonIds.includes(personId) &&
      pk.programs.some((p) => programIds.includes(p.id))
    ) {
      return `Уже в пакете «${pk.name}»`
    }
  }
  if (target.kind === 'package') {
    for (const p of c.programs) {
      if (
        p.listenersCount > 0 &&
        programIds.includes(p.id) &&
        p.selectedPersonIds.includes(personId)
      ) {
        return `«${p.shortName || p.name}» уже выбрана отдельно`
      }
    }
  }
  return undefined
}

/** Сотрудник, выбранный в окне редактора, — строка для списка слушателей (ФИО и должность). */
function pickedEmployee(e: TrainingProgramEmployee): TrainingSelectedEmployee {
  return {
    personId: e.personId,
    fullName: e.fullName,
    departmentName: e.departmentName,
    position: e.position,
    snils: '',
    gender: '',
    programs: [],
    hasConsent: false,
  }
}

/** Подсказка редактора: что согласует другая сторона и что нужно у поданного заказа. */
export function editorHint(submitted: boolean, side: ChangeSide): string {
  const approver = side === 'client' ? 'Центрика' : 'клиент'
  const base = `Внесите изменения и отправьте их на согласование — состав заявки изменится, когда их согласует ${approver}.`
  if (!submitted) return base
  // Клиент вносит данные новых слушателей прямо здесь (ответ заказчицы 04.11 к п.1.4); у команды
  // их вносит клиент в карточке сотрудника.
  return side === 'client'
    ? `${base} Назовите всех слушателей; данные новых слушателей заполните здесь же, в блоке «Данные новых слушателей».`
    : `${base} Назовите всех слушателей; у новых слушателей должны быть заполнены данные (СНИЛС, дата рождения, пол, образование, e-mail, согласие на обработку персональных данных, а для программ, где нужно высшее или среднее профессиональное образование, — документ о нём) — их вносит клиент в карточке сотрудника.`
}

/** Слушатель из карточки сотрудника — для заполнения данных нового слушателя в редакторе. */
function listenerFromCard(e: EmployeeCard, needsEducationDocument: boolean): TrainingSelectedEmployee {
  return {
    personId: e.id,
    fullName: e.fullName,
    departmentName: e.departmentName,
    position: e.position,
    snils: e.snils,
    birthDate: e.birthDate || undefined,
    gender: e.gender,
    email: e.email,
    education: e.education,
    programs: [],
    hasConsent: Boolean(e.consent?.hasConsent),
    consentFileName: e.consent?.consentFileName,
    consentUrl: e.consent?.consentUrl,
    educationDocument: e.educationDocument,
    needsEducationDocument,
  }
}

/** Сотрудники, которых нужно обучить по программе с базой «ВО/СПО» (в пакете — по его программам). */
function personsNeedingEducationDocument(c: ChangeComposition): Set<string> {
  const out = new Set<string>()
  for (const p of c.programs) {
    if (p.educationBase === 'vo_spo') p.selectedPersonIds.forEach((id) => out.add(id))
  }
  for (const pk of c.packages) {
    if (pk.programs.some((p) => p.educationBase === 'vo_spo')) pk.selectedPersonIds.forEach((id) => out.add(id))
  }
  return out
}

/** Все слушатели состава (программы и пакеты). */
function compositionPersons(c: ChangeComposition): Set<string> {
  const out = new Set<string>()
  c.programs.forEach((p) => p.selectedPersonIds.forEach((id) => out.add(id)))
  c.packages.forEach((p) => p.selectedPersonIds.forEach((id) => out.add(id)))
  return out
}

/**
 * Данные новых слушателей поданного заказа (ответ заказчицы 04.11 к п.1.4: «Нужна возможность при
 * изменении состава заявки тут же заполнить все недостающие данные»): карточки слушателей шага
 * «Заполнение данных» — только для добавленных в этом изменении, данные сохраняются в карточку
 * сотрудника сразу. Возвращает разметку блока и признак, что у кого-то заполнено не всё.
 */
function useNewListenersData(
  enabled: boolean,
  initialPersons: ReadonlySet<string>,
  composition: ChangeComposition,
) {
  const persons = compositionPersons(composition)
  const newIds = [...persons].filter((id) => !initialPersons.has(id))
  const needsDoc = personsNeedingEducationDocument(composition)
  const [cards, setCards] = useState<Map<string, EmployeeCard>>(new Map())
  const [failed, setFailed] = useState<string | null>(null)
  const [saving, setSaving] = useState(0)
  const [invalid, setInvalid] = useState<Set<string>>(new Set())
  const requested = useRef(new Set<string>())
  const key = newIds.join(',')

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    for (const id of key ? key.split(',') : []) {
      if (requested.current.has(id)) continue
      requested.current.add(id)
      companyApi
        .getEmployee(id, controller.signal)
        .then((card) => setCards((prev) => new Map(prev).set(id, card)))
        .catch((err) => {
          requested.current.delete(id)
          if (!controller.signal.aborted) setFailed(getErrorMessage(err, 'Не удалось загрузить данные сотрудника'))
        })
    }
    return () => controller.abort()
  }, [enabled, key])

  const listeners = enabled
    ? newIds
        .map((id) => cards.get(id))
        .filter((c): c is EmployeeCard => Boolean(c))
        .map((c) => listenerFromCard(c, needsDoc.has(c.id)))
    : []
  const loading = enabled && listeners.length < newIds.length && !failed
  const incomplete =
    enabled &&
    (loading || saving > 0 || invalid.size > 0 || listeners.some((l) => listenerMissing(l).length > 0))

  function patch(personId: string, p: Partial<TrainingSelectedEmployee>) {
    setCards((prev) => {
      const card = prev.get(personId)
      if (!card) return prev
      const next: EmployeeCard = {
        ...card,
        snils: p.snils ?? card.snils,
        birthDate: p.birthDate ?? card.birthDate,
        gender: (p.gender as EmployeeCard['gender']) ?? card.gender,
        email: p.email ?? card.email,
        education: p.education ?? card.education,
        educationDocument: 'educationDocument' in p ? p.educationDocument : card.educationDocument,
        consent:
          'hasConsent' in p
            ? { personId, hasConsent: Boolean(p.hasConsent), consentFileName: p.consentFileName, consentUrl: p.consentUrl }
            : card.consent,
      }
      return new Map(prev).set(personId, next)
    })
  }

  const block =
    enabled && newIds.length > 0 ? (
      <section aria-label="Данные новых слушателей" id="new-listeners-data" className="flex scroll-mt-24 flex-col gap-2">
        <h3 className="text-15 font-semibold text-txt">Данные новых слушателей</h3>
        <p className="text-13 text-txt-secondary">
          Данные сохраняются в карточки сотрудников сразу. Отправить изменения можно, когда у новых
          слушателей всё заполнено.
        </p>
        {failed ? <Alert variant="danger">{failed}</Alert> : null}
        {loading ? <p className="text-13 text-txt-secondary">Загрузка данных сотрудников…</p> : null}
        <ul className="divide-y divide-line rounded-10 border border-line bg-panel">
          {listeners.map((l) => (
            <ListenerCard
              key={l.personId}
              employee={l}
              departmentMissing={isUnspecifiedDepartment(l.departmentName)}
              disabled={false}
              onSaving={(d) => setSaving((n) => n + d)}
              onPatch={patch}
              onInvalidDraft={(id, bad) =>
                setInvalid((prev) => {
                  const next = new Set(prev)
                  if (bad) next.add(id)
                  else next.delete(id)
                  return next
                })
              }
            />
          ))}
        </ul>
      </section>
    ) : null
  return { block, incomplete, count: newIds.length }
}

/**
 * Редактор изменений состава. `initial` — состав сейчас (у шага мастера — позиции каталога с
 * выбранными слушателями), `employees` — слушатели заказа (ФИО по id), `locked` — позиции с
 * выпущенным итоговым документом. `propose` отправляет желаемый состав; по успеху вызывающий
 * закрывает редактор.
 */
export function TrainingChangeEditor({
  orderId,
  side,
  submitted,
  initial,
  employees,
  locked,
  propose,
  onCancel,
  loadEmployees,
  lockedPrograms,
  counterTo,
}: {
  orderId: string
  side: ChangeSide
  /** Заказ подан («На проверке», «В работе»): все места названы, данные новых слушателей полны. */
  submitted: boolean
  initial: ChangeComposition
  employees: TrainingSelectedEmployee[]
  locked?: ReadonlySet<string>
  /** Отправить желаемый состав; в режиме «свой вариант» — с причиной отказа клиенту. */
  propose: (items: TrainingChangeItemInput[], reason?: string) => Promise<void>
  onCancel: () => void
  /**
   * «Предложить свой вариант» (команда, ответ заказчицы 04.11 к п.1.4): предложение клиента, на
   * которое отвечают, — его протокол показывается над редактором; отправка отклоняет его с причиной
   * и вносит свой вариант.
   */
  counterTo?: TrainingChange
  /**
   * Сотрудники клиента для окна выбора — у команды по командному маршруту; создавать сотрудников
   * команда не может (их заводит клиент).
   */
  loadEmployees?: (
    target: EmployeePickerTarget,
    signal: AbortSignal,
  ) => Promise<TrainingProgramEmployee[]>
  /** Программы с выпущенным итоговым документом — их не добавить ни отдельно, ни в пакете. */
  lockedPrograms?: ReadonlySet<string>
}) {
  const [composition, setComposition] = useState<ChangeComposition>(initial)
  const [picked, setPicked] = useState<TrainingSelectedEmployee[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [reason, setReason] = useState('')

  const allEmployees = useMemo(() => {
    const byId = new Map(employees.map((e) => [e.personId, e]))
    picked.forEach((e) => {
      if (!byId.has(e.personId)) byId.set(e.personId, e)
    })
    return [...byId.values()]
  }, [employees, picked])
  const names = useMemo(
    () => new Map(allEmployees.map((e) => [e.personId, e.fullName])),
    [allEmployees],
  )
  const summary = changeSummary(initial, composition, (id) => names.get(id) ?? 'сотрудник')
  const views = [...itemViews(composition).values()]
  // У поданного заказа все места должны быть названы (как при подтверждении данных).
  const unnamed = submitted ? views.filter((v) => v.persons.length < v.places) : []
  const empty = views.length === 0
  const initialPersons = useMemo(() => compositionPersons(initial), [initial])
  // Данные новых слушателей поданного заказа клиент вносит здесь же (ответ заказчицы 04.11 к п.1.4).
  const newListeners = useNewListenersData(submitted && side === 'client', initialPersons, composition)
  const canSend =
    summary.length > 0 && unnamed.length === 0 && !empty && !sending && !newListeners.incomplete

  function onPicked(rows: TrainingProgramEmployee[]) {
    setPicked((prev) => [
      ...prev,
      ...rows.filter((r) => !prev.some((p) => p.personId === r.personId)).map(pickedEmployee),
    ])
  }

  async function send() {
    setError(null)
    setFieldErrors([])
    setSending(true)
    try {
      await propose(changeItems(composition), counterTo ? reason.trim() : undefined)
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setError(err.message || 'Сервер отклонил изменения — проверьте состав.')
        setFieldErrors((err.details ?? []).map((d) => d.message))
      } else {
        setError(getErrorMessage(err, 'Не удалось отправить изменения'))
      }
      setSending(false)
    }
  }

  // «Что изменится», ошибки и кнопки — под «Выбранными программами», закреплены вместе с ними (ответ
  // заказчицы 04.11 к п.4.1–4.3): не нужно листать каталог до низа страницы.
  const footer = (
    <div className="flex flex-col gap-2">
      <section aria-label="Что изменится">
        <h4 className="text-13 font-semibold text-txt">Что изменится</h4>
        {summary.length === 0 ? (
          <p className="text-13 text-txt-secondary">Изменений пока нет.</p>
        ) : (
          <ul className="list-disc pl-5 text-13">
            {summary.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        )}
      </section>
      {empty ? (
        <Alert variant="danger">В заявке должна остаться хотя бы одна программа или пакет.</Alert>
      ) : null}
      {unnamed.length > 0 ? (
        <Alert variant="danger" title="Укажите всех слушателей">
          <ul className="list-disc pl-5">
            {unnamed.map((v) => (
              <li key={v.key}>
                {capitalize(v.label)}: {namedOfPlaces(v.persons.length, v.places).toLowerCase()}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
      {newListeners.incomplete && newListeners.count > 0 ? (
        <div className="flex flex-wrap items-center gap-2 text-13 text-red">
          <span>Заполните данные новых слушателей — без них изменения не отправить.</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              document.getElementById('new-listeners-data')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
            }
          >
            К данным новых слушателей
          </Button>
        </div>
      ) : null}
      {counterTo ? (
        // Причина отказа клиенту (ответ заказчицы 04.11 к п.1.4) — её увидит клиент вместе с вашим
        // вариантом.
        <Field label="Причина отказа (необязательно)" hint="Её увидит клиент" className="my-0">
          {(props) => (
            <textarea
              {...props}
              value={reason}
              maxLength={REASON_MAX}
              rows={2}
              disabled={sending}
              onChange={(e) => setReason(e.target.value)}
              className={TEXTAREA_CLASS}
            />
          )}
        </Field>
      ) : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {fieldErrors.length > 0 ? (
        <ul className="ml-4 list-disc text-13 text-red">
          {fieldErrors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button disabled={!canSend} onClick={() => void send()}>
          {sending ? 'Отправка…' : counterTo ? 'Отклонить и предложить свой вариант' : 'Отправить на согласование'}
        </Button>
        <Button variant="secondary" disabled={sending} onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </div>
  )

  return (
    <Card header={counterTo ? 'Свой вариант состава' : 'Изменение состава заявки'}>
      <div className="flex flex-col gap-4">
        {counterTo ? (
          <Alert variant="info" title="Вы отвечаете на изменения клиента">
            <p>
              Клиент предлагал: {counterTo.protocol.map((e) => e.text).join('; ')}. Внесите свой
              вариант — изменения клиента будут отклонены, ваш вариант уйдёт клиенту на согласование.
            </p>
          </Alert>
        ) : (
          <Alert variant="info">{editorHint(submitted, side)}</Alert>
        )}
        {newListeners.block}
        <TrainingStepPrograms
          orderId={orderId}
          draft={!submitted}
          programs={composition.programs}
          packages={composition.packages}
          selectedEmployees={allEmployees}
          mode="proposal"
          onPatch={(patch) => setComposition((c) => applyCompositionPatch(c, patch))}
          onPicked={onPicked}
          blockedFor={(target, personId) => changeBlockedFor(composition, target, personId)}
          locked={locked}
          loadEmployees={loadEmployees}
          allowCreate={side === 'client'}
          lockedPrograms={lockedPrograms}
          showPrice={side === 'team'}
          selectionFooter={footer}
        />
      </div>
    </Card>
  )
}
